import React from "react";
import ContentLoader from "react-content-loader";
import { useMediaQuery } from "react-responsive";

// Logo
export const LogoSkeleton: React.FC = () => (
	<ContentLoader
		speed={2}
		width={40}
		height={40}
		viewBox="0 0 40 40"
		backgroundColor="#eaeaea"
		foregroundColor="#ecebeb"
	>
		<circle cx="20" cy="20" r="18" />
	</ContentLoader>
);

// Badge
export const BadgeSkeleton: React.FC = () => (
	<div style={{ marginLeft: 'auto' }}>
		<ContentLoader
			speed={2}
			width={28}
			height={28}
			viewBox="0 0 28 28"
			backgroundColor="#eaeaea"
			foregroundColor="#ecebeb"
		>
			<circle cx="14" cy="14" r="10" />
		</ContentLoader>
	</div>
);

// Hamburger
export const HamburgerSkeleton: React.FC = () => (
	<ContentLoader
		speed={2}
		width={32}
		height={32}
		viewBox="0 0 32 32"
		backgroundColor="#eaeaea"
		foregroundColor="#ecebeb"
	>
		<rect x="0" y="0" width="32" height="32" />
	</ContentLoader>
);

// Switch Wrapper
export const SwitchSkeleton: React.FC = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <ContentLoader
      speed={2}
      width={200}
      height={48}
      backgroundColor="#eaeaea"
      foregroundColor="#ecebeb"
    >
      <rect x="0" y="0" rx="10" ry="10" width="100%" height="48" />
    </ContentLoader>
  </div>
);

// Card
export const CardSkeleton: React.FC = () => (
	<ContentLoader
		speed={2}
		width="100%"
		height={140}
		backgroundColor="#eaeaea"
		foregroundColor="#ecebeb"
	>
		<rect x="0" y="0" rx="10" ry="10" width="100%" height="140" />
	</ContentLoader>
);

// Text
export const TextSkeleton: React.FC<{ width?: string }> = ({ width = "100%" }) => (
	<ContentLoader
		speed={2}
		width={width}
		height={20}
		backgroundColor="#eaeaea"
		foregroundColor="#ecebeb"
	>
		<rect x="0" y="0" rx="6" ry="6" width={width} height="20" />
	</ContentLoader>
);

// Button
export const ButtonSkeleton: React.FC = () => (
	<ContentLoader
		speed={2}
		width="100%"
		height={40}
		backgroundColor="#eaeaea"
		foregroundColor="#ecebeb"
	>
		<rect x="0" y="0" rx="10" ry="10" width="100%" height="30" />
	</ContentLoader>
);

// Home - Responsive Skeleton Loader
type SkeletonLayout = {
	viewBox: string;
	rects: { x: string | number; y: string | number; width: string | number; height: string | number; rx: number; ry: number }[];
};

const skeletonLayouts: Record<string, SkeletonLayout> = {
	xxl: {
		viewBox: "0 0 1620 1067",
		rects: [
			{ x: 0, y: 0, width: 1620, height: 358.412, rx: 0, ry: 0 },
			{ x: 190, y: 422.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 190, y: 451.412, width: 466.544, height: 34, rx: 16, ry: 16 },
			{ x: 190, y: 501.412, width: 457.544, height: 18, rx: 8, ry: 8 },
			{ x: 834, y: 422.412, width: 596, height: 266, rx: 16, ry: 16 },
			{ x: 190, y: 800.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 190, y: 829.412, width: 466.544, height: 34, rx: 16, ry: 16 },
			{ x: 190, y: 879.412, width: 457.544, height: 18, rx: 8, ry: 8 },
			{ x: 834, y: 800.412, width: 596, height: 266, rx: 16, ry: 16 },
		],
	},
	xl: {
		viewBox: "0 0 1212 1059",
		rects: [
			{ x: 0, y: 0, width: 1212, height: 358.412, rx: 0, ry: 0 },
			{ x: 96, y: 422.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 96, y: 451.412, width: 466.544, height: 34, rx: 16, ry: 16 },
			{ x: 96, y: 501.412, width: 457.544, height: 18, rx: 8, ry: 8 },
			{ x: 630, y: 422.412, width: 486, height: 266, rx: 16, ry: 16 },
			{ x: 96, y: 792.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 96, y: 821.412, width: 466.544, height: 34, rx: 16, ry: 16 },
			{ x: 96, y: 871.412, width: 457.544, height: 18, rx: 8, ry: 8 },
			{ x: 630, y: 792.412, width: 486, height: 266, rx: 16, ry: 16 },
		],
	},
	lg: {
		viewBox: "0 0 1066 1035",
		rects: [
			{ x: 0, y: 0, width: 1066, height: 358.412, rx: 0, ry: 0 },
			{ x: 80, y: 414.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 80, y: 443.412, width: 437, height: 34, rx: 16, ry: 16 },
			{ x: 80, y: 493.412, width: 428, height: 18, rx: 8, ry: 8 },
			{ x: 549, y: 414.412, width: 437, height: 266, rx: 16, ry: 16 },
			{ x: 80, y: 768.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 80, y: 797.412, width: 437, height: 34, rx: 16, ry: 16 },
			{ x: 80, y: 847.412, width: 428, height: 18, rx: 8, ry: 8 },
			{ x: 549, y: 768.412, width: 437, height: 266, rx: 16, ry: 16 },
		],
	},
	md: {
		viewBox: "0 0 900 971",
		rects: [
			{ x: 0, y: 0, width: 900, height: 358.412, rx: 0, ry: 0 },
			{ x: 32, y: 382.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 32, y: 411.412, width: 386, height: 34, rx: 16, ry: 16 },
			{ x: 32, y: 461.412, width: 386, height: 18, rx: 8, ry: 8 },
			{ x: 466, y: 382.412, width: 402, height: 266, rx: 16, ry: 16 },
			{ x: 32, y: 704.412, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 32, y: 733.412, width: 386, height: 34, rx: 16, ry: 16 },
			{ x: 32, y: 783.412, width: 386, height: 18, rx: 8, ry: 8 },
			{ x: 466, y: 704.412, width: 402, height: 266, rx: 16, ry: 16 },
		],
	},
	xs: {
		viewBox: "0 0 375 1120",
		rects: [
			{ x: 0, y: 0, width: 375, height: 242.006, rx: 0, ry: 0 },
			{ x: 16, y: 290.006, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 16, y: 319.006, width: 343, height: 34, rx: 16, ry: 16 },
			{ x: 16, y: 369.006, width: 343, height: 18, rx: 8, ry: 8 },
			{ x: 16, y: 403.006, width: 343, height: 266, rx: 16, ry: 16 },
			{ x: 16, y: 741.006, width: 205.186, height: 14, rx: 8, ry: 8 },
			{ x: 16, y: 770.006, width: 343, height: 34, rx: 16, ry: 16 },
			{ x: 16, y: 820.006, width: 343, height: 18, rx: 8, ry: 8 },
			{ x: 16, y: 854.006, width: 343, height: 266, rx: 16, ry: 16 },
		],
	},
};

// HomeCardSkeletons
export const HomeCardSkeletons: React.FC = () => {
	const isXXL = useMediaQuery({ minWidth: 1800 });
	const isXL = useMediaQuery({ minWidth: 1400, maxWidth: 1799 });
	const isLG = useMediaQuery({ minWidth: 1100, maxWidth: 1399 });
	const isMD = useMediaQuery({ minWidth: 800, maxWidth: 1099 });
	const isXS = useMediaQuery({ maxWidth: 799 });

	let layout: SkeletonLayout = skeletonLayouts.xs;
	if (isXXL) layout = skeletonLayouts.xxl;
	else if (isXL) layout = skeletonLayouts.xl;
	else if (isLG) layout = skeletonLayouts.lg;
	else if (isMD) layout = skeletonLayouts.md;

	return (
		<ContentLoader
			speed={2}
			viewBox={layout.viewBox}
			backgroundColor="#eaeaea"
			foregroundColor="#ecebeb"
			width="100%"
			height="auto"
			style={{ width: "100%", height: "auto", marginBottom: 100 }}
		>
			{layout.rects.map((r, i) => (
				<rect key={i} {...r} />
			))}
		</ContentLoader>
	);
};
